import React, { useState, useEffect, useCallback } from 'react';
import { Card, Spinner, Row, Col, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import ObjectivesProgressCard from './ObjectivesProgressCard';
import ObjectivesVsRealChart from './ObjectivesVsRealChart';
import ObjectivesTrendChart from './ObjectivesTrendChart';
import apiClient from '../../../services/api';

const ObjectivesSummaryWidget = ({
  clientId,
  dateFrom,
  dateTo,
  showTrend = true,
  maxCards = 6,
  compact = false,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (clientId) params.append('clientId', String(clientId));
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);

      const response = await apiClient.get(
        `/budget/dashboard/objectives-summary?${params.toString()}`
      );
      const result = response.data?.data || response.data;

      if (result?.success === false) {
        setError(result.error || 'Error cargando objetivos');
      } else {
        setData(result?.data || result);
      }
    } catch (err) {
      console.error('Error loading objectives summary:', err);
      setError('Error al cargar el resumen de objetivos');
    } finally {
      setLoading(false);
    }
  }, [clientId, dateFrom, dateTo]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center py-4">
          <Spinner animation="border" size="sm" variant="primary" className="me-2" />
          <span className="text-muted">Cargando objetivos...</span>
        </Card.Body>
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center py-4">
          <FontAwesomeIcon icon="exclamation-triangle" className="text-warning me-2" />
          <span className="text-muted">{error}</span>
        </Card.Body>
      </Card>
    );
  }

  const objectives = data?.objectives || [];

  if (objectives.length === 0) {
    return (
      <Card className="shadow-sm border-0">
        <Card.Body className="text-center py-4">
          <FontAwesomeIcon icon="bullseye" size="2x" className="text-300 mb-2" />
          <p className="text-muted mb-0">No hay objetivos configurados</p>
          <small className="text-400">
            Crea objetivos desde el módulo de Performance para verlos aquí
          </small>
        </Card.Body>
      </Card>
    );
  }

  const aggregated = data?.aggregatedByUnit || {};
  const onTrack = objectives.filter((o) => o.progressPct >= 90 && o.progressPct <= 110).length;
  const atRisk = objectives.filter((o) => o.progressPct < 90 && o.progressPct >= 70).length;
  const critical = objectives.filter((o) => o.progressPct < 70).length;
  const exceeded = objectives.filter((o) => o.progressPct > 110).length;

  return (
    <div>
      {/* Header con estadísticas rápidas */}
      <Card className="shadow-sm border-0 mb-3">
        <Card.Header className="bg-white border-bottom py-2 d-flex justify-content-between align-items-center flex-wrap">
          <h6 className="mb-0">
            <FontAwesomeIcon icon="bullseye" className="me-2 text-primary" />
            Progreso de Objetivos
            <Badge bg="secondary" className="ms-2 fs-10">{objectives.length}</Badge>
          </h6>
          <div className="d-flex gap-2 flex-wrap mt-1 mt-md-0">
            {onTrack > 0 && (
              <Badge bg="success" className="fs-10">
                <FontAwesomeIcon icon="check-circle" className="me-1" />
                {onTrack} en meta
              </Badge>
            )}
            {exceeded > 0 && (
              <Badge bg="info" className="fs-10">
                <FontAwesomeIcon icon="arrow-up" className="me-1" />
                {exceeded} superados
              </Badge>
            )}
            {atRisk > 0 && (
              <Badge bg="warning" className="fs-10">
                <FontAwesomeIcon icon="exclamation-triangle" className="me-1" />
                {atRisk} en riesgo
              </Badge>
            )}
            {critical > 0 && (
              <Badge bg="danger" className="fs-10">
                <FontAwesomeIcon icon="times-circle" className="me-1" />
                {critical} críticos
              </Badge>
            )}
          </div>
        </Card.Header>
        {Object.keys(aggregated).length > 0 && (
          <Card.Body className="py-2">
            <Row className="g-2">
              {Object.entries(aggregated).map(([unit, vals]) => {
                const pct = vals.objective > 0 ? (vals.real / vals.objective) * 100 : 0;
                return (
                  <Col xs={6} md={3} key={unit}>
                    <div className="text-center p-2 bg-light rounded">
                      <small className="text-uppercase text-500 fw-bold fs-10 d-block">{unit}</small>
                      <div className="fs-11">
                        <span className="text-primary fw-bold">
                          {vals.real >= 1000 ? (vals.real / 1000).toFixed(1) + 'K' : Math.round(vals.real)}
                        </span>
                        <span className="text-400 mx-1">/</span>
                        <span className="text-700">
                          {vals.objective >= 1000 ? (vals.objective / 1000).toFixed(1) + 'K' : Math.round(vals.objective)}
                        </span>
                      </div>
                      <small className={`fw-bold fs-10 text-${pct >= 90 ? 'success' : pct >= 70 ? 'warning' : 'danger'}`}>
                        {pct.toFixed(0)}%
                      </small>
                    </div>
                  </Col>
                );
              })}
            </Row>
          </Card.Body>
        )}
      </Card>

      {/* Progress Cards */}
      <div className="mb-3">
        <ObjectivesProgressCard objectives={objectives} maxItems={maxCards} />
      </div>

      {/* Charts */}
      {!compact && (
        <Row className="g-3">
          <Col lg={showTrend ? 6 : 12}>
            <ObjectivesVsRealChart objectives={objectives} />
          </Col>
          {showTrend && (
            <Col lg={6}>
              <ObjectivesTrendChart objectives={objectives} />
            </Col>
          )}
        </Row>
      )}
    </div>
  );
};

export default ObjectivesSummaryWidget;
